import { useState } from "react";
import Layout from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { PageDecorations } from "@/components/Decorations";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePageContent } from "@/lib/usePageContent";
import { RichText } from "@/components/RichText";
import { BookOpen } from "lucide-react";
import kalevaKiertueImg from "@/assets/kaleva-kokoomus-kiertue.jpg";
import kalevaRotuaariImg from "@/assets/kaleva-rotuaari-kuhisee.jpg";
import kalevaPresidenttiImg from "@/assets/kaleva-presidenttiehdokkaille.jpg";
import knEnsimmainenImg from "@/assets/kn-ensimmainen-ehdolle.jpg";



type MediaLink = {
  href: string;
  image?: string;
  title: string;
  source: string;
  dateLabel: string;
  ctaLabel?: string;
};


const MEDIA_LINKS: Record<string, MediaLink[]> = {
  lotta_mediassa: [
    {
      href: "https://www.kaleva.fi/iso-joukko-kokoomuksen-eturivin-poliitikkoja-viera/12511182",
      image: kalevaKiertueImg,
      title:
        "Iso joukko kokoomuksen eturivin poliitikkoja vieraili Oulussa – Kiertueen ideana nostattaa henkeä maakunnassa",
      source: "Kaleva",
      dateLabel: "27.1.2026",
    },
    {
      href: "https://www.kokoomusnuoret.fi/tiedotteet/2025/11/ensimmainen-kokoomusnuori-ehdolle-eduskuntavaaleihin/",
      image: knEnsimmainenImg,
      title: "Ensimmäinen kokoomusnuori ehdolle eduskuntavaaleihin!",
      source: "Kokoomusnuoret",
      dateLabel: "28.11.2025",
    },
    {
      href: "https://www.kaleva.fi/karkkia-ampareita-ja-kasisaippuaa-rotuaari-kuhisee/11583408",
      image: kalevaRotuaariImg,
      title:
        "Karkkia, ämpäreitä ja käsisaippuaa – Rotuaari kuhisee ehdokkaita, lähiöissä sitä vastoin hiljaista",
      source: "Kaleva",
      dateLabel: "6.4.2025",
    },
    {
      href: "https://www.kaleva.fi/oululaiset-auli-jaasko-ja-lotta-ellonen-ovat-uhran/6217471",
      image: kalevaPresidenttiImg,
      title:
        "Oululaiset Auli Jääskö ja Lotta Ellonen ovat uhranneet presidenttiehdokkaille ison siivun vapaa-ajastaan – \"Sunnuntaina nähdään, mihin se riittää\"",
      source: "Kaleva",
      dateLabel: "9.2.2024",
    },
  ],

  julkaisut: [
    {
      href: "https://urn.fi/URN:NBN:fi:oulu-202606294979",
      title: "Kokemuksellisuus kasvatus- ja matkailuympäristöjä yhdistävänä tekijänä",
      source: "Oulun yliopisto",
      dateLabel: "8.3.2024",
      ctaLabel: "Lue julkaisu Oulun yliopiston julkaisuarkistossa →",
    },
  ],
};



const MediaLinkCard = ({ link }: { link: MediaLink }) => (
  <a
    href={link.href}
    target="_blank"
    rel="noreferrer"
    className="group block overflow-hidden rounded-2xl border border-border/40 bg-card/80 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
  >
    {link.image ? (
      <div className="overflow-hidden bg-muted">
        <img
          src={link.image}
          alt={link.title}
          loading="lazy"
          className="h-56 w-full object-cover object-top transition duration-500 group-hover:scale-[1.02] md:h-64"
        />
      </div>
    ) : (
      <div className="flex h-32 items-center justify-center bg-primary/10 md:h-40">
        <BookOpen className="h-12 w-12 text-primary md:h-14 md:w-14" strokeWidth={1.5} aria-hidden="true" />
      </div>
    )}
    <div className="p-6 md:p-7">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary">
        {link.source} · {link.dateLabel}
      </p>
      <h3 className="font-display text-lg font-bold italic leading-snug text-secondary md:text-xl">
        {link.title}
      </h3>
      <p className="mt-4 text-sm italic text-secondary underline-offset-4 group-hover:underline">
        {link.ctaLabel ?? `Lue kirjoitus ${link.source === "Kokoomusnuoret" ? "Kokoomusnuorten sivuilta" : `${link.source}ssa`} →`}
      </p>
    </div>
  </a>
);





const FALLBACK = {
  hero_badge: "Blogi",
  hero_title: "Ajankohtaista",
  placeholder_title: "Tulossa pian",
  placeholder_subtitle: "Seuraa miuta somessa!",
};

type SourceMeta = {
  match: RegExp;
  badges: string[];
  dateLabel: string;
  title: string;
  link?: string;
  linkLabel?: string;
  bylineName: string;
  bylineRole: string;
  authors?: { name: string; role: string }[];
};

const SOURCES: SourceMeta[] = [
  {
    match: /pakkojasenyys-ylioppilaskunnissa/i,
    badges: ["Lukijalta", "Mielipidekirjoitus", "Korkeakoulupolitiikka"],
    dateLabel: "Julkaistu Kalevassa 20.9.2025.",
    title:
      "Pakkojäsenyys ylioppilaskunnissa on tullut eläkeikään – opiskelijan on voitava itse valita, millaiselle vaikuttamistyölle antaa vähäiset rahansa.",
    link: "https://www.kaleva.fi/pakkojasenyys-ylioppilaskunnissa-on-tullut-elakeik/11777771",
    linkLabel: "Lue kirjoitus Kalevassa",
    bylineName: "Lotta Ellonen",
    bylineRole:
      "Kokoomusnuorten liittohallituksen jäsen, kasvatustieteen kandidaatti, Oulun yliopisto, Oulu",
  },
  {
    match: /Keväisin terveisin, Lotta Ellonen/,
    badges: [],
    dateLabel: "13.4.2025 Oulussa",
    title: "Kirje sinulle - kunta ja aluevaalit 2025",
    bylineName: "Lotta Ellonen",
    bylineRole: "",
  },
  {
    match: /kokoomusnuoret\.fi/i,
    badges: ["Kannanotto"],
    dateLabel: "Julkaistu Kokoomusnuorten sivuilla 12.12.2024.",
    title:
      "Naiset kuuluvat sinne mihin miehetkin, myös maanpuolustuksessa – Helsingin, Satakunnan, Savo-Karjalan, Kaakkois-Suomen, Oulun Läänin, Lapin ja Hämeen Kokoomusnuoret jyrähtävät",
    link: "https://www.kokoomusnuoret.fi/kannanotot/2024/12/naiset-kuuluvat-sinne-mihin-miehetkin-myos-maanpuolustuksessa-helsingin-satakunnan-savo-karjalan-kaakkois-suomen-oulun-laanin-lapin-ja-hameen-kokoomusnuoret-jyrahtavat/",
    linkLabel: "Lue koko kannanotto kokoomusnuoret.fi:ssä",
    bylineName: "Savo-Karjalan Kokoomusnuoret, Satakunnan Kokoomusnuoret, Kaakkois-Suomen Kokoomusnuoret, Oulun Läänin Kokoomusnuoret, Hämeen Kokoomusnuoret, Lapin Kokoomusnuoret ja Helsingin Kokoomusnuoret",
    bylineRole: "",
  },
  {
    match: /voidaanko-erottamalla-rakentaa-syrjinnasta-vapaata/i,
    badges: ["Lukijalta", "Mielipidekirjoitus", "Kokoomusnuoret"],
    dateLabel: "Julkaistu Kalevassa 17.10.2023.",
    title:
      "Voidaanko erottamalla rakentaa syrjinnästä vapaata yhteisöä?",
    link: "https://www.kaleva.fi/voidaanko-erottamalla-rakentaa-syrjinnasta-vapaata/5963520",
    linkLabel: "Lue kirjoitus Kalevassa",
    bylineName: "Aamos Valjus ja Lotta Ellonen",
    bylineRole: "",
    authors: [
      { name: "Aamos Valjus", role: "Oulun Läänin Kokoomusnuorten varapuheenjohtaja 2023, kauppatieteiden opiskelija" },
      { name: "Lotta Ellonen", role: "Oulun Opiskelevien Kokoomusnuorten puheenjohtaja 2023, kasvatustieteen opiskelija" },
    ],
  },
];

type Post = {
  id: string;
  slug: string | null;
  title: string;
  excerpt: string | null;
  body: string | null;
  image_url: string | null;
  published_at: string;
  category: string | null;
};

const PostCard = ({ p }: { p: Post }) => {
  const [expanded, setExpanded] = useState(false);
  const source = SOURCES.find((s) => typeof p.body === "string" && s.match.test(p.body));

  const paras = (p.body ?? "").split(/\n\s*\n/);
  const linkRe = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/;
  const last = paras[paras.length - 1]?.trim() ?? "";
  const m = last.match(linkRe);
  const bodyParas = m ? paras.slice(0, -1) : paras;

  const COLLAPSED_COUNT = source?.match.test("kokoomusnuoret.fi") ? 0 : p.slug === "kirje-sinulle-kunta-ja-aluevaalit-2025" ? 1 : 2;
  const canCollapse = bodyParas.length > COLLAPSED_COUNT;
  const shownParas = !expanded && canCollapse ? bodyParas.slice(0, COLLAPSED_COUNT) : bodyParas;

  const renderPara = (para: string, i: number) => {
    const isPakkojasenyysParagraph = para.startsWith("Pakkojäsenyyden varaan rakennetun järjestelmän sijaan");
    const trimmed = para.trim();
    const isPullQuote = /^["“].*["”]$/.test(trimmed);
    const signatureMatch = trimmed.match(/^\*([^*]+)\*$/);

    if (signatureMatch) {
      return (
        <p
          key={i}
          className="mt-10 font-display text-xl italic leading-snug text-secondary md:text-2xl"
        >
          {signatureMatch[1]}
        </p>
      );
    }


    if (isPullQuote) {
      const inner = trimmed.replace(/^["“]|["”]$/g, "");
      return (
        <blockquote
          key={i}
          className="mt-6 mb-10 border-l-4 border-primary pl-5 font-display text-xl italic leading-snug text-secondary"
        >
          ”<RichText as="span" text={inner} boldClassName="font-bold" />”
        </blockquote>
      );
    }

    if (isPakkojasenyysParagraph) {
      return (
        <p key={i}>
          <span className="font-bold uppercase">Pakkojäsenyyden</span>{" "}
          {para.replace(/^Pakkojäsenyyden\s+/, "")}
        </p>
      );
    }

    return <RichText key={i} as="p" text={para} />;
  };

  return (
    <article className="rounded-2xl border border-border/40 bg-card/80 p-6 pt-10 shadow-sm md:p-8 md:pt-12">
      {p.image_url && (
        <img src={p.image_url} alt="" className="mb-6 h-64 w-full rounded-xl object-cover" />
      )}
      {source && source.badges.length > 0 && (
        <div className="mb-8 flex flex-wrap items-center gap-3">
          {source.badges.map((b) => (
            <span
              key={b}
              className="inline-block rounded-full bg-primary px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground"
            >
              {b}
            </span>
          ))}
        </div>
      )}
      <p className="mb-2 text-sm text-muted-foreground">
        {source
          ? source.dateLabel
          : new Date(p.published_at).toLocaleDateString("fi-FI", { year: "numeric", month: "long", day: "numeric" })}
      </p>
      <div className="mb-6 h-[3px] w-full rounded-full bg-primary" aria-hidden="true" />
      <h3 className="mb-10 font-display text-2xl font-bold italic leading-snug text-secondary md:text-3xl">
        {source ? source.title : p.title}
      </h3>

      {p.excerpt && (
        <RichText
          as="p"
          text={p.excerpt}
          className={
            source
              ? "mb-10 border-l-4 border-primary pl-5 font-display text-lg font-semibold italic leading-snug text-secondary md:mb-12 md:text-xl"
              : "mb-4 text-sm italic text-muted-foreground"
          }
        />
      )}

      {p.body && (
        <>
          <div className={`relative ${!expanded && canCollapse ? "max-h-[420px] overflow-hidden" : ""}`}>
            <div className="space-y-4 leading-relaxed text-foreground/85">
              {shownParas.map(renderPara)}
            </div>
            {!expanded && canCollapse && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-card" />
            )}
          </div>

          {canCollapse && !expanded && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-sm transition hover:brightness-95"
              >
                Lue lisää
              </button>
            </div>
          )}

          {expanded && (m || source) && (
            <>
              {(source?.bylineName ?? "Lotta Ellonen") && (
                <>
                  <div className="mt-8 space-y-4">
                    {source?.authors ? (
                      source.authors.map((a) => (
                        <div key={a.name}>
                          <p className="font-display text-lg font-bold italic text-secondary">
                            {a.name}
                          </p>
                          {a.role && (
                            <p className="mt-2 text-sm italic text-muted-foreground">
                              {a.role}
                            </p>
                          )}
                        </div>
                      ))
                    ) : (
                      <div>
                        <p className="font-display text-lg font-bold italic text-secondary">
                          {source?.bylineName ?? "Lotta Ellonen"}
                        </p>
                        {(source?.bylineRole ?? "") && (
                          <p className="mt-2 text-sm italic text-muted-foreground">
                            {source?.bylineRole}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mt-3 h-[3px] w-full rounded-full bg-primary" aria-hidden="true" />
                </>
              )}
              {(m || source?.link) && (
                <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                  <p className="text-sm italic">
                    <a
                      href={source?.link ?? m![2]}
                      target="_blank"
                      rel="noreferrer"
                      className="text-secondary underline-offset-4 hover:underline visited:text-purple-700"
                    >
                      {(m?.[1] ?? source?.linkLabel ?? "Lue lisää") + " →"}
                    </a>
                  </p>
                </div>
              )}
            </>
          )}

          {expanded && canCollapse && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold uppercase tracking-wider text-primary-foreground shadow-sm transition hover:brightness-95"
              >
                Näytä vähemmän
              </button>
            </div>
          )}
        </>
      )}
    </article>
  );
};

const Blog = () => {
  const { t } = usePageContent("blog", FALLBACK);
  const { data: posts } = useQuery({
    queryKey: ["public_blog_posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id,slug,title,excerpt,body,image_url,published_at,category")
        .eq("published", true)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const CATEGORIES: { key: string; title: string; bg: string }[] = [
    { key: "omasta_kynasta", title: "Omasta kynästä", bg: "hsl(20 45% 91%)" },
    { key: "lotta_mediassa", title: "Lotta mediassa", bg: "hsl(206 42% 90%)" },
    { key: "julkaisut", title: "Julkaisut", bg: "hsl(140 28% 88%)" },
  ];

  return (
    <Layout>
      <PageHero badge={t("hero_badge")}>{t("hero_title")}</PageHero>

      <section className="relative overflow-hidden px-6 py-16 lg:px-10" style={{ background: "linear-gradient(180deg, hsl(39,82%,92%) 0%, hsl(42,86%,78%) 100%)" }}>
        <PageDecorations />
        <div className="relative mx-auto max-w-4xl">
          <div className="space-y-16">
            {CATEGORIES.map(({ key, title, bg }) => {
              const items = (posts ?? []).filter((p) => (p.category ?? "omasta_kynasta") === key);
              const mediaLinks = MEDIA_LINKS[key] ?? [];
              const hasContent = items.length > 0 || mediaLinks.length > 0;
              return (
                <div key={key} className="mb-16 last:mb-0">
                  <div className="relative mb-10 flex items-center gap-3">
                    <h2 className="font-display text-3xl font-bold italic text-foreground md:text-4xl">
                      {title}
                    </h2>
                  </div>
                  <div
                    className="rounded-[1.6rem] border border-border/60 p-8 shadow-sm md:p-10"
                    style={{ background: bg }}
                  >
                    {hasContent ? (
                      <div className="space-y-6">
                        {mediaLinks.map((link) => (
                          <MediaLinkCard key={link.href} link={link} />
                        ))}
                        {items.map((p) => (
                          <PostCard key={p.id} p={p as Post} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-lg leading-relaxed text-foreground/85">Tulossa pian.</p>
                    )}
                  </div>

                </div>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
